import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'tl.theme';

function resolve(theme: Theme): boolean {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  return theme === 'dark';
}

/**
 * The inline script in index.html has already applied the right class before
 * first paint; this hook only keeps it in sync afterwards.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? 'system',
  );

  const apply = useCallback((next: Theme) => {
    document.documentElement.classList.toggle('dark', resolve(next));
  }, []);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      if (next === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
      apply(next);
    },
    [apply],
  );

  useEffect(() => {
    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => apply('system');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [theme, apply]);

  return { theme, setTheme, isDark: resolve(theme) };
}
